import { useState } from "react";
import { X, Loader2, FileText, Ruler, FileDown } from "lucide-react";
import { api, fileUrl } from "@/lib/api";
import { toast } from "sonner";

export default function TechPackModal({ design, techpack: initialTP, onClose }) {
  const [tp, setTp] = useState(initialTP);
  const [loading, setLoading] = useState(false);
  const [pdfBusy, setPdfBusy] = useState(false);

  const generate = async () => {
    setLoading(true);
    try {
      const { data } = await api.post(`/designs/${design.id}/techpack`);
      setTp(data);
      toast.success("Tech pack generated");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to generate tech pack");
    } finally {
      setLoading(false);
    }
  };

  const downloadPatternPdf = async () => {
    setPdfBusy(true);
    try {
      const { data } = await api.post(`/designs/${design.id}/pattern-pdf`);
      // Fetch the PDF as blob to force download with title
      const res = await fetch(fileUrl(data.pdf_path));
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${(design.title || "pattern").replace(/[^\w-]+/g, "_")}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("Pattern pack downloaded");
    } catch (err) {
      console.error(err);
      toast.error(err?.response?.data?.detail || "PDF generation failed");
    } finally { setPdfBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-[100] bg-zinc-950/90 backdrop-blur-md flex items-start md:items-center justify-center overflow-y-auto py-10 px-4" data-testid="techpack-modal">
      <div className="w-full max-w-6xl bg-zinc-50 text-zinc-950 border border-zinc-200 shadow-2xl relative">
        <button onClick={onClose} className="absolute top-4 right-4 w-10 h-10 flex items-center justify-center bg-zinc-950 text-zinc-50 hover:bg-lime-400 hover:text-zinc-950 z-10" data-testid="techpack-close-btn">
          <X className="w-4 h-4" />
        </button>

        <div className="p-8 md:p-12">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-8 border-b border-zinc-300 pb-8 mb-8">
            <div className="md:col-span-8">
              <div className="overline text-zinc-500 mb-2">Tech Pack · Production Sheet</div>
              <h2 className="font-serif text-4xl md:text-6xl leading-[0.9]">{design.title}</h2>
              <div className="mt-3 overline text-zinc-500">
                {design.category}{design.fabric ? ` · ${design.fabric}` : ""}{design.silhouette ? ` · ${design.silhouette}` : ""}{design.length ? ` · ${design.length}` : ""}
              </div>
            </div>
            <div className="md:col-span-4 flex md:justify-end items-start gap-3">
              <div className="overline text-zinc-500 font-mono">DOC N°{(design.id || "").slice(0, 6).toUpperCase()}</div>
              {tp && (
                <button
                  onClick={downloadPatternPdf}
                  disabled={pdfBusy}
                  className={`overline flex items-center gap-2 px-4 py-2 border ${pdfBusy ? "border-zinc-300 text-zinc-400" : "border-zinc-950 hover:bg-zinc-950 hover:text-zinc-50"} transition-colors`}
                  data-testid="techpack-pdf-btn"
                >
                  {pdfBusy ? <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Drafting</> : <><FileDown className="w-3.5 h-3.5" /> Pattern PDF</>}
                </button>
              )}
            </div>
          </div>

          {!tp ? (
            <div className="text-center py-16">
              <FileText className="w-12 h-12 mx-auto text-zinc-400 mb-6" strokeWidth={1.2} />
              <div className="font-serif italic text-3xl text-zinc-700 mb-3">No tech pack yet.</div>
              <p className="text-zinc-500 max-w-md mx-auto mb-8">Generate a technical flat sketch with front/back views, measurements, materials, and construction notes — everything a maker needs to sew this piece.</p>
              <button
                onClick={generate}
                disabled={loading}
                className={`inline-flex items-center gap-2 px-8 py-4 rounded-full overline ${loading ? "bg-zinc-300 text-zinc-500" : "bg-lime-400 text-zinc-950 hover:bg-zinc-950 hover:text-lime-400"} transition-colors`}
                data-testid="techpack-generate-btn"
              >
                {loading ? <><Loader2 className="w-4 h-4 animate-spin" /> Drafting the pattern…</> : <><Ruler className="w-4 h-4" /> Generate Tech Pack</>}
              </button>
            </div>
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
              <div className="lg:col-span-7">
                <div className="overline text-zinc-500 mb-3">Flat Sketch — Front / Back</div>
                <div className="relative aspect-[4/3] bg-white border border-zinc-300">
                  <img src={fileUrl(tp.flat_sketch_path)} alt="flat sketch" className="absolute inset-0 w-full h-full object-contain" data-testid="techpack-flat-image" />
                </div>
                <div className="mt-4 grid grid-cols-2 md:grid-cols-3 gap-4">
                  <div className="border-t border-zinc-300 pt-3">
                    <div className="overline text-zinc-500">Difficulty</div>
                    <div className="font-serif text-2xl">{tp.difficulty || "—"}</div>
                  </div>
                  <div className="border-t border-zinc-300 pt-3">
                    <div className="overline text-zinc-500">Yardage</div>
                    <div className="font-serif text-2xl italic">{tp.estimated_yield || "—"}</div>
                  </div>
                  <div className="border-t border-zinc-300 pt-3 col-span-2 md:col-span-1">
                    <div className="overline text-zinc-500">Care</div>
                    <div className="text-sm mt-1 leading-snug">{tp.care_instructions || "—"}</div>
                  </div>
                </div>
              </div>

              <div className="lg:col-span-5 space-y-6">
                <Section title="Materials" testid="techpack-materials">
                  {(tp.materials || []).length === 0 ? <Empty/> : (
                    <ul className="divide-y divide-zinc-200">
                      {tp.materials.map((m, i) => (
                        <li key={`mat-${m.name}-${i}`} className="py-2 flex items-start justify-between gap-4">
                          <span className="font-serif italic text-lg">{m.name}</span>
                          <span className="text-xs text-zinc-500 text-right">{m.notes || ""}</span>
                        </li>
                      ))}
                    </ul>
                  )}
                </Section>

                <Section title="Measurements (Size M)" testid="techpack-measurements">
                  {(tp.measurements || []).length === 0 ? <Empty/> : (
                    <ul className="divide-y divide-zinc-200 font-mono text-sm">
                      {tp.measurements.map((m, i) => (
                        <li key={`meas-${m.point}-${i}`} className="py-2 flex items-center justify-between">
                          <span className="uppercase tracking-wide text-zinc-600">{m.point}</span>
                          <span className="text-zinc-950">{m.value}</span>
                        </li>
                      ))}
                    </ul>
                  )}
                </Section>

                <Section title="Trims & Hardware" testid="techpack-trims">
                  {(tp.trims || []).length === 0 ? <Empty/> : (
                    <ul className="divide-y divide-zinc-200 text-sm">
                      {tp.trims.map((t, i) => (
                        <li key={`trim-${t.name}-${i}`} className="py-2 flex items-center justify-between">
                          <span>{t.name}</span>
                          <span className="text-zinc-500 font-mono">×{t.qty}</span>
                        </li>
                      ))}
                    </ul>
                  )}
                </Section>

                <Section title="Construction Notes" testid="techpack-construction">
                  {(tp.construction_notes || []).length === 0 ? <Empty/> : (
                    <ol className="space-y-2 text-sm leading-relaxed list-decimal list-inside">
                      {tp.construction_notes.map((n, i) => (
                        <li key={`step-${i}`}>{n}</li>
                      ))}
                    </ol>
                  )}
                </Section>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function Section({ title, children, testid }) {
  return (
    <div data-testid={testid}>
      <div className="overline text-zinc-500 border-b border-zinc-950 pb-2 mb-3">{title}</div>
      {children}
    </div>
  );
}

function Empty() {
  return <div className="text-sm text-zinc-400 italic">Not specified</div>;
}
